import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ThemeMode = 'light' | 'dark';
export type ThemePreference = ThemeMode | 'auto';

type ThemeModeContextValue = {
  mode: ThemePreference;
  resolvedMode: ThemeMode;
  setMode: (mode: ThemePreference) => void;
  toggleMode: () => void;
};

const STORAGE_KEY = 'erp-theme-mode';

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

const getStoredMode = (): ThemePreference | null => {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'dark' || stored === 'light' || stored === 'auto') return stored;
  return null;
};

const getSystemMode = (): ThemeMode => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getInitialMode = (): ThemePreference => {
  return getStoredMode() ?? 'auto';
};

export const ThemeModeProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  // eslint-disable-next-line react/hook-use-state -- Using setModeInternal to create a wrapped setMode
  const [mode, setModeInternal] = useState<ThemePreference>(getInitialMode);
  const [systemMode, setSystemMode] = useState<ThemeMode>(getSystemMode);
  // Track if user has explicitly set a preference (vs. just using system default)
  const hasUserPreference = useRef<boolean>(getStoredMode() !== null);

  // Wrapped setMode that marks user preference
  const setMode = useCallback((newMode: ThemePreference): void => {
    hasUserPreference.current = true;
    setModeInternal(newMode);
  }, []);

  const toggleMode = useCallback((): void => {
    hasUserPreference.current = true;
    setModeInternal((prev) => {
      if (prev === 'dark') return 'light';
      if (prev === 'light') return 'dark';
      return systemMode === 'dark' ? 'light' : 'dark';
    });
  }, [systemMode]);

  // Prevent flash on initial load by disabling transitions
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    document.documentElement.classList.add('no-transition');
    // Remove class after first paint to enable transitions
    requestAnimationFrame(() => {
      document.documentElement.classList.remove('no-transition');
    });
  }, []);

  const resolvedMode: ThemeMode = mode === 'auto' ? systemMode : mode;

  // Sync to localStorage and DOM
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Only persist to localStorage if user has explicitly set a preference
    if (hasUserPreference.current) {
      window.localStorage.setItem(STORAGE_KEY, mode);
    }
    document.documentElement.setAttribute('data-theme', resolvedMode);
  }, [mode, resolvedMode]);

  // Listen for OS-level theme preference changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent): void => {
      setSystemMode(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return (): void => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const value = useMemo(
    () => ({
      mode,
      resolvedMode,
      setMode,
      toggleMode,
    }),
    [mode, resolvedMode, setMode, toggleMode],
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useThemeMode = (): ThemeModeContextValue => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeModeProvider');
  }
  return context;
};
